import React from 'react';
import { Box, Typography, Alert, Button } from '@mui/material';
import { AdminPanelSettings, Block } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import Login from './Login';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const AccessDenied: React.FC<{ onLogout: () => void }> = ({ onLogout }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      p: 4
    }}
  >
    <Block sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
    <Typography variant="h4" gutterBottom color="error">
      Access Denied
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
      You don't have admin privileges to access this panel.
      <br />
      Please contact your administrator for access.
    </Typography>
    <Button variant="contained" onClick={onLogout} startIcon={<AdminPanelSettings />}>
      Try Different Account
    </Button>
  </Box>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, isAdmin, loading, logout } = useAuth();

  // Show loading while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // If not authenticated, show login
  if (!currentUser) {
    return <Login />;
  }

  // If authenticated but not admin, show access denied
  if (!isAdmin) {
    return <AccessDenied onLogout={logout} />;
  }

  // If authenticated and admin, show protected content
  return <>{children}</>;
};

export default ProtectedRoute;
